import Swal from "sweetalert2";
import { getFiltros } from "./openFiltrosPedidoModal";
import store from "@/store";

async function uploadPedidoExcel(
    axios: any,
    apiRoute: string,
    marca: string,
    matchField: string,
    file: File
) {
    const token = localStorage.getItem("authToken");
    const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
    };

    const formData = new FormData();
    formData.append("file", file);
    formData.append("marca", marca);
    formData.append("match_field", matchField);

    return axios.post(`${apiRoute}/pedido/carga-excel/upload`, formData, { headers });
}

async function downloadPedidoExcelTemplate(axios: any, apiRoute: string, matchField: string) {
    const token = localStorage.getItem("authToken");
    const response = await axios.get(`${apiRoute}/pedido/carga-excel/template`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { match_field: matchField },
        responseType: "blob",
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "plantilla_carga_pedido.xlsx");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

export default function openPedidoCargaExcelModal(axios: any, apiRoute: string, onSuccess?: () => void) {
    getFiltros(axios, apiRoute).then((response: any) => {
        const marcas: any[] = response.data?.marcas ?? [];

        const marcaOptions = marcas
            .map((m: any) => {
                const nombre = typeof m === "string" ? m : (m?.marca ?? "");
                return nombre ? `<option value="${nombre}">${nombre}</option>` : "";
            })
            .join("");

        const html = `
            <div class="text-left text-sm">
                <div class="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5 mb-4 text-xs text-blue-700 leading-relaxed">
                    Sube un Excel con código/barra y cantidad para agregar productos al carrito de la marca
                    seleccionada. Si un producto ya está en el carrito, se <strong>suma</strong> la cantidad
                    a la que ya tenía.
                </div>

                <div class="mb-4">
                    <label class="block text-sm font-semibold text-gray-700 mb-1.5">Marca</label>
                    <select id="pce_marca" class="w-full border border-gray-300 rounded-lg p-2.5 text-sm bg-white focus:ring focus:ring-blue-200 focus:border-blue-500">
                        <option value="">Selecciona una marca</option>
                        ${marcaOptions}
                    </select>
                </div>

                <div class="mb-4">
                    <label class="block text-sm font-semibold text-gray-700 mb-1.5">Campo de coincidencia</label>
                    <select id="pce_match_field" class="w-full border border-gray-300 rounded-lg p-2.5 text-sm bg-white focus:ring focus:ring-blue-200 focus:border-blue-500">
                        <option value="codprod">Código de producto</option>
                        <option value="barra">Código de barras</option>
                    </select>
                </div>

                <div class="mb-4 flex items-center justify-between">
                    <label class="block text-sm font-semibold text-gray-700">Archivo (xlsx o xls)</label>
                    <button type="button" id="pce_download_template" class="text-xs text-blue-600 hover:text-blue-800 font-medium underline">
                        Descargar plantilla
                    </button>
                </div>
                <div>
                    <input id="pce_file" type="file" accept=".xlsx,.xls"
                        class="w-full text-sm border border-gray-300 rounded-lg file:mr-3 file:py-2 file:px-3 file:border-0 file:rounded-l-lg file:bg-gray-100 file:text-gray-700 file:text-sm hover:file:bg-gray-200" />
                </div>
            </div>
        `;

        Swal.fire({
            title: "Cargar pedido por Excel",
            html,
            width: 480,
            showCancelButton: true,
            confirmButtonText: "Cargar",
            cancelButtonText: "Cancelar",
            reverseButtons: true,
            focusConfirm: false,
            customClass: {
                confirmButton: "custom-confirm-button",
            },
            didOpen: () => {
                const downloadBtn = document.getElementById("pce_download_template") as HTMLButtonElement | null;
                downloadBtn?.addEventListener("click", async () => {
                    const matchField = (document.getElementById("pce_match_field") as HTMLSelectElement)?.value ?? "codprod";
                    try {
                        await downloadPedidoExcelTemplate(axios, apiRoute, matchField);
                    } catch (error) {
                        Swal.fire({ icon: "error", title: "No se pudo descargar la plantilla" });
                    }
                });
            },
            preConfirm: async () => {
                const marca = (document.getElementById("pce_marca") as HTMLSelectElement)?.value;
                const matchField = (document.getElementById("pce_match_field") as HTMLSelectElement)?.value;
                const fileInput = document.getElementById("pce_file") as HTMLInputElement;
                const file = fileInput?.files?.[0];

                if (!marca) {
                    Swal.showValidationMessage("Debes seleccionar una marca.");
                    return false;
                }

                if (!file) {
                    Swal.showValidationMessage("Debes seleccionar un archivo.");
                    return false;
                }

                try {
                    const uploadResponse = await uploadPedidoExcel(axios, apiRoute, marca, matchField, file);
                    return { ...uploadResponse.data, __marca: marca };
                } catch (error: any) {
                    const msg = error?.response?.data?.message ?? "No se pudo cargar el archivo.";
                    Swal.showValidationMessage(msg);
                    return false;
                }
            },
        }).then((result) => {
            if (!result.isConfirmed || !result.value) {
                return;
            }

            const resumen = result.value;
            const noEncontrados = resumen.no_encontrados_count ?? 0;

            Swal.fire({
                icon: noEncontrados > 0 ? "warning" : "success",
                title: "Carga completada",
                html: `
                    <div class="text-left text-sm">
                        <div class="flex gap-3 mb-2">
                            <div class="flex-1 bg-green-50 border border-green-100 rounded-lg px-3 py-2 text-center">
                                <div class="text-2xl font-semibold text-green-600">${resumen.agregados ?? 0}</div>
                                <div class="text-xs text-green-700">Agregados al carrito</div>
                            </div>
                            <div class="flex-1 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-center">
                                <div class="text-2xl font-semibold text-amber-600">${noEncontrados}</div>
                                <div class="text-xs text-amber-700">No encontrados</div>
                            </div>
                        </div>
                        ${noEncontrados > 0
                            ? `<p class="text-xs text-gray-500 mt-2">${(resumen.no_encontrados || []).join(", ")}</p>`
                            : ""
                        }
                    </div>
                `,
            }).then(() => {
                if (onSuccess) {
                    onSuccess();
                } else {
                    store.dispatch('orders/fetchCartItems', { apiRoute, branch: resumen.__marca ?? "" });
                    store.dispatch('orders/fetchCartItemsQuantity', { apiRoute });
                }
            });
        });
    });
}
